import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Textarea } from "../ui/textarea";
import { Upload, FileText, Loader2, Sparkles, ArrowRight, ArrowLeft, Check, Wand2, Lock, Target, X, Users } from "lucide-react";
import { uploadFile, extractBrief, classifyDoc, errorMessage } from "../../lib/api";
import { track } from "../../lib/analytics";
import { AccuracyBadge } from "./AccuracyBadge";
import { UploadProgress } from "./UploadProgress";
import { toast } from "sonner";

const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:opacity-60";
const btnOutline = "inline-flex items-center justify-center gap-2 rounded-lg bg-white text-zinc-800 font-medium ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 transition-colors disabled:opacity-60";

const Field = ({ label, value, onChange, placeholder, min = "56px", optional, testId }) => (
  <div>
    <label className="overline text-zinc-400">
      {label} {optional && <span className="normal-case tracking-normal text-zinc-400/80">(optional)</span>}
    </label>
    <Textarea
      data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-2 rounded-lg border-zinc-200 resize-none focus-visible:ring-indigo-500" style={{ minHeight: min }}
    />
  </div>
);

const ContextField = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="overline text-zinc-400">{label} <span className="normal-case tracking-normal text-zinc-400/80">(optional)</span></label>
    <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-2 w-full h-10 rounded-lg border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
  </div>
);

export const UploadPanel = ({ onAnalyze }) => {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [outcomes, setOutcomes] = useState("");
  const [rubric, setRubric] = useState("");
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("Pasted text.txt");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [briefFilename, setBriefFilename] = useState("");
  const [consentCorpus, setConsentCorpus] = useState(false);
  const [essayExtraction, setEssayExtraction] = useState(null);
  const [guideText, setGuideText] = useState("");
  const [guideFilename, setGuideFilename] = useState("");
  const [guideBusy, setGuideBusy] = useState(false);
  const [supDocs, setSupDocs] = useState([]);
  const [supBusy, setSupBusy] = useState(false);
  // Optional context (helps admin calibration; safe to leave blank)
  const [university, setUniversity] = useState("");
  const [module, setModule] = useState("");
  const [subject, setSubject] = useState("");
  const [moduleLevel, setModuleLevel] = useState("");
  const [faculty, setFaculty] = useState("");
  const [department, setDepartment] = useState("");
  const [showContext, setShowContext] = useState(false);
  // Group-assignment contribution mode
  const [scope, setScope] = useState("full"); // "full" | "contribution"
  // Upload progress state — shows the % bar + rotating labels for the
  // essay/brief/guide/supporting uploads so large files don't feel frozen.
  // key: "essay" | "brief" | "guide" | "supporting"; null = no upload in flight
  const [progress, setProgress] = useState(null);
  const briefRef = useRef(null);
  const essayRef = useRef(null);
  const guideRef = useRef(null);
  const supRef = useRef(null);

  const words = (text.trim().match(/\b\w+\b/g) || []).length;

  // Build the onUploadProgress callback for a given upload slot.
  const onProgress = (key, file) => (p) => {
    setProgress({
      key,
      filename: file?.name,
      sizeMB: file ? file.size / 1024 / 1024 : 0,
      // While bytes are still in flight → "uploading"; once we hit 100 the
      // server takes over (extract/OCR) → switch to "processing".
      phase: p.percent != null && p.percent >= 100 ? "processing" : "uploading",
      percent: p.percent,
    });
  };
  const clearProgress = () => setProgress(null);

  const handleGuide = async (file) => {
    if (!file) return;
    setGuideBusy(true);
    try {
      const up = await uploadFile(file, onProgress("guide", file));
      setGuideText(up.text || ""); setGuideFilename(file.name);
      toast.success(`Assignment guide added — "${file.name}"`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally { setGuideBusy(false); clearProgress(); }
  };

  const handleSupporting = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setSupBusy(true);
    try {
      for (const file of list) {
        const up = await uploadFile(file, onProgress("supporting", file));
        let doc_type = "Other";
        try { doc_type = (await classifyDoc(up.text, file.name)).doc_type; } catch (_) {}
        setSupDocs((prev) => [...prev, { filename: file.name, text: up.text, doc_type }]);
      }
      toast.success("Supporting documents classified & added.");
    } catch (e) {
      toast.error(errorMessage(e, "Could not read a supporting document."));
    } finally { setSupBusy(false); clearProgress(); }
  };

  const handleBrief = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const up = await uploadFile(file, onProgress("brief", file));
      // Post-upload: server is interpreting the brief with an LLM — reflect that.
      setProgress((p) => p ? { ...p, phase: "processing" } : p);
      const b = await extractBrief(up.text);
      const q = (b.question || "").trim();
      setTitle(b.title || ""); setQuestion(q);
      setOutcomes(b.learning_outcomes || ""); setRubric(b.rubric || "");
      setBriefFilename(file.name);
      track("assignment_brief_uploaded", { question_detected: !!q });
      if (q) {
        toast.success(`Brief read — "${file.name}". Requirements extracted below.`);
      } else {
        toast.warning("We read the file but couldn't detect a clear assignment question. Please paste it into the Assignment question box below so we can predict your grade.");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file. Try a .docx or text-based .pdf, or paste the question below."));
    } finally {
      setExtracting(false); clearProgress();
    }
  };

  const handleEssay = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadFile(file, onProgress("essay", file));
      setText(data.text); setFilename(data.filename); setEssayExtraction(data.extraction || null);
      track("essay_uploaded", { words: data.word_count });
      toast.success(`Loaded ${data.filename} — ${data.word_count} words`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally {
      setUploading(false); clearProgress();
    }
  };

  const submit = () => {
    if (words < 30) { toast.error("Paste or upload at least ~30 words of your essay."); return; }
    onAnalyze({ text, filename, title, question, outcomes, rubric, consent_corpus: consentCorpus, extraction: essayExtraction, assignment_guide: guideText, supporting_docs: supDocs,
      university, module, subject, module_level: moduleLevel, faculty, department,
      submission_scope: scope });
  };

  return (
    <div className="p-6 sm:p-8" data-testid="upload-panel">
      <div className="mb-6"><AccuracyBadge variant="landing" /></div>

      {/* Global upload progress — surfaces the real % during the network
          upload and a rotating status message once the server takes over.
          Only mounted while an upload is in flight so it doesn't clutter
          the panel afterwards. */}
      {progress && (
        <div className="mb-4">
          <UploadProgress
            phase={progress.phase}
            percent={progress.percent}
            filename={progress.filename}
            sizeMB={progress.sizeMB}
          />
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-3 mb-6" data-testid="stepper">
        {[1, 2].map((n) => (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-2 ${step === n ? "text-indigo-700" : "text-zinc-400"}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${step > n ? "bg-indigo-600 text-white" : step === n ? "bg-indigo-100 text-indigo-700" : "bg-zinc-100 text-zinc-400"}`}>
                {step > n ? <Check size={14} /> : n}
              </span>
              <span className="text-sm font-medium">{n === 1 ? "Assignment brief" : "Your essay"}</span>
            </div>
            {n === 1 && <div className="flex-1 h-px bg-zinc-200" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="step-1">
          {/* Submission scope — Individual Contribution Mode */}
          <div className="rounded-xl border border-zinc-200 p-4 mb-4" data-testid="scope-selector">
            <p className="text-sm font-medium text-zinc-900 mb-1">What are you analysing?</p>
            <p className="text-xs text-zinc-500 mb-3">Pick "My contribution" if this is a group assignment and you only wrote part of it — we won&apos;t penalise missing sections written by teammates.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { id: "full", label: "Entire assignment", sub: "Default — one person&apos;s complete submission" },
                { id: "contribution", label: "My contribution to a group assignment", sub: "We&apos;ll grade only your section" },
              ].map((opt) => (
                <button key={opt.id} type="button" onClick={() => setScope(opt.id)}
                  data-testid={`scope-${opt.id}`}
                  className={`text-left p-3 rounded-lg border transition-colors ${scope === opt.id ? "border-indigo-600 bg-indigo-50" : "border-zinc-200 hover:border-zinc-300 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${scope === opt.id ? "border-indigo-600" : "border-zinc-300"}`}>
                      {scope === opt.id && <span className="w-2 h-2 rounded-full bg-indigo-600" />}
                    </span>
                    <span className="text-sm font-medium text-zinc-900">{opt.label}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 ml-6" dangerouslySetInnerHTML={{ __html: opt.sub }} />
                </button>
              ))}
            </div>
            {scope === "contribution" && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 leading-relaxed" data-testid="scope-contribution-note">
                <b>Contribution mode active.</b> Upload only <b>your own section</b> of the group assignment. The assignment brief is <b>recommended</b> so we can grade against the same criteria your lecturer will use. If you have the full group assignment for context, you can attach it below as a supporting document (optional).
              </div>
            )}
          </div>

          <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 mb-4 flex gap-3" data-testid="brief-accuracy-note">
            <Target size={18} className="text-indigo-600 mt-0.5 shrink-0" />
            <p className="text-sm text-zinc-700 leading-relaxed">
              For the most accurate examiner-style assessment, upload <b>both your assignment brief and your
              assignment</b>. The assignment brief contains the assessment criteria used by university markers —
              a grade prediction requires it.
            </p>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-zinc-500 mb-4">
            <Lock size={12} /> Your work is analysed privately and used only to provide feedback.
          </p>

          <div className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Wand2 size={18} className="text-indigo-600" /></div>
              <div>
                <p className="text-sm font-medium text-zinc-900">Upload your assignment brief</p>
                <p className="text-xs text-zinc-500">We'll identify the assignment question, learning outcomes, and marking criteria automatically.</p>
              </div>
            </div>
            <input ref={briefRef} type="file" accept=".docx,.pdf,.txt,.md" className="hidden"
              data-testid="brief-input" onChange={(e) => handleBrief(e.target.files?.[0])} />
            <button type="button" data-testid="upload-brief-btn" disabled={extracting}
              onClick={() => briefRef.current?.click()} className={`${btnPrimary} px-4 py-2.5 text-sm`}>
              {extracting ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {extracting ? "Reading brief…" : "Upload brief"}
            </button>
          </div>

          {briefFilename && (
            <div className="px-4 py-2.5 flex items-center gap-2 text-sm text-zinc-500" data-testid="brief-filename">
              <FileText size={14} className="text-emerald-600" />
              <span className="text-zinc-800">{briefFilename}</span>
              <span className="text-xs">— attached</span>
            </div>
          )}

          <div className="mt-4 rounded-xl border border-zinc-200 p-5 space-y-4">
            <Field label="Assignment title" value={title} onChange={setTitle} optional testId="title-input"
              placeholder="e.g. Critically evaluate leadership theories" min="44px" />
            <Field label="Assignment question" value={question} onChange={setQuestion} testId="question-input"
              placeholder="Paste the exact assignment question or brief…" min="72px" />
            <Field label="Learning outcomes" value={outcomes} onChange={setOutcomes} optional testId="outcomes-input"
              placeholder="Optional — paste the learning outcomes…" />
            <GroupRubricPresetPicker onPick={(preset) => setRubric(preset.rubric)} currentRubric={rubric} />
            <Field label="Marking rubric / criteria" value={rubric} onChange={setRubric} optional testId="rubric-input"
              placeholder="Optional — paste the marking rubric so we can grade against it…" />
          </div>

          {/* Assignment Guide + Supporting Documents */}
          <div className="mt-4 rounded-xl border border-zinc-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between flex-wrap gap-3 border-b border-zinc-100">
              <div>
                <p className="text-sm font-medium text-zinc-900">Assignment guide <span className="text-xs text-zinc-400">(optional)</span></p>
                <p className="text-xs text-zinc-500">Extra tutor guidance — improves grading accuracy.</p>
              </div>
              <input ref={guideRef} type="file" accept=".docx,.pdf,.pptx,.xlsx,.txt,.md" className="hidden"
                data-testid="guide-input" onChange={(e) => handleGuide(e.target.files?.[0])} />
              <button type="button" data-testid="upload-guide-btn" disabled={guideBusy}
                onClick={() => guideRef.current?.click()} className={`${btnOutline} px-4 py-2 text-sm`}>
                {guideBusy ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Upload guide
              </button>
            </div>
            {guideFilename && (
              <p className="px-4 py-2 text-xs text-emerald-600 flex items-center gap-1.5 border-b border-zinc-100" data-testid="guide-filename">
                <FileText size={13} /> {guideFilename} — attached
              </p>
            )}
            <div className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-medium text-zinc-900">Supporting documents <span className="text-xs text-zinc-400">(optional)</span></p>
                <p className="text-xs text-zinc-500">Rubric, module handbook, lecture slides, case studies… auto-classified.</p>
              </div>
              <input ref={supRef} type="file" multiple accept=".docx,.pdf,.pptx,.xlsx,.txt,.md" className="hidden"
                data-testid="supporting-input" onChange={(e) => handleSupporting(e.target.files)} />
              <button type="button" data-testid="upload-supporting-btn" disabled={supBusy}
                onClick={() => supRef.current?.click()} className={`${btnOutline} px-4 py-2 text-sm`}>
                {supBusy ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Add documents
              </button>
            </div>
            {supDocs.length > 0 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="supporting-list">
                {supDocs.map((d, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-zinc-100 rounded-full px-2.5 py-1">
                    <Check size={12} className="text-emerald-600" /> {d.doc_type}: {d.filename}
                    <button onClick={() => setSupDocs((p) => p.filter((_, idx) => idx !== i))} data-testid={`remove-sup-${i}`}>
                      <X size={12} className="text-zinc-400 hover:text-zinc-700" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ACADEMIC LEVEL — primary field. This is the biggest driver of
              grade accuracy; keep it visible and required-feeling (though
              still submittable empty so the backend can infer from the brief). */}
          <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="level-panel">
            <label className="text-sm font-medium text-zinc-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">!</span>
              Academic Level
            </label>
            <p className="text-xs text-zinc-600 mt-1 mb-3">
              We grade very differently for a Foundation-level submission vs. a Final Year one. Please confirm your level so MyAE applies the right rubric.
            </p>
            <select
              data-testid="academic-level-primary"
              value={moduleLevel}
              onChange={(e) => setModuleLevel(e.target.value)}
              className="w-full h-11 rounded-lg border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select level (or let MyAE infer from the brief)</option>
              <option value="3">Foundation (Level 3)</option>
              <option value="4">Year 1 (Level 4)</option>
              <option value="5">Year 2 (Level 5)</option>
              <option value="6">Final Year (Level 6)</option>
              <option value="7">Postgraduate (Level 7)</option>
            </select>
          </div>

          <button onClick={() => setStep(2)} data-testid="next-step-btn"
            className={`${btnPrimary} mt-6 w-full h-12 text-base`}>
            Continue to your essay <ArrowRight size={18} />
          </button>
          <p className="text-xs text-zinc-500 mt-3 text-center">
            No brief available? No problem — enter the assignment question manually or continue directly to your essay.
          </p>

          {/* Optional context — helps us calibrate future grade predictions */}
          <div className="mt-4 rounded-xl border border-zinc-200" data-testid="context-panel">
            <button type="button" onClick={() => setShowContext((v) => !v)}
              data-testid="context-toggle"
              className="w-full p-4 flex items-center justify-between text-left hover:bg-zinc-50 transition-colors rounded-xl">
              <div>
                <p className="text-sm font-medium text-zinc-900">Assignment context <span className="text-xs text-zinc-400">(optional)</span></p>
                <p className="text-xs text-zinc-500">Module, university and other extras — helps improve grade accuracy over time.</p>
              </div>
              <span className={`text-zinc-400 transition-transform ${showContext ? "rotate-90" : ""}`}>›</span>
            </button>
            {showContext && (
              <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="context-fields">
                <ContextField label="University" value={university} onChange={setUniversity} testId="ctx-university" placeholder="e.g. University of Bath" />
                <ContextField label="Faculty" value={faculty} onChange={setFaculty} testId="ctx-faculty" placeholder="e.g. School of Management" />
                <ContextField label="Department" value={department} onChange={setDepartment} testId="ctx-department" placeholder="e.g. Marketing" />
                <ContextField label="Module" value={module} onChange={setModule} testId="ctx-module" placeholder="e.g. MN30349" />
                <ContextField label="Subject" value={subject} onChange={setSubject} testId="ctx-subject" placeholder="e.g. Marketing, Nursing, Law" />
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="step-2">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-900 mb-1">Your essay</h2>
          <p className="text-zinc-500 text-sm mb-5 max-w-xl">
            Paste your essay below or upload your document. Your AI examiner will review your work against
            the assignment brief and marking criteria.
          </p>
          <div className="rounded-xl border border-zinc-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <label className="overline text-zinc-400">Paste or upload your essay</label>
              <span className="font-mono text-xs text-zinc-500 tabular-nums">{words} words</span>
            </div>
            <Textarea
              data-testid="essay-input" value={text}
              onChange={(e) => { setText(e.target.value); if (filename !== "Pasted text.txt") setFilename("Pasted text.txt"); }}
              placeholder="Paste your essay text here, or upload a .docx / .pdf below…"
              className="rounded-lg border-zinc-200 resize-y min-h-[260px] leading-relaxed focus-visible:ring-indigo-500 text-[14px]"
            />
            <input ref={essayRef} type="file" accept=".docx,.pdf,.pptx,.xlsx,.txt,.md" className="hidden"
              data-testid="file-input" onChange={(e) => handleEssay(e.target.files?.[0])} />
            <div className="flex items-center gap-3 mt-3">
              <button type="button" data-testid="upload-doc-btn"
                onClick={() => essayRef.current?.click()} disabled={uploading} className={`${btnOutline} px-4 py-2.5 text-sm`}>
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {uploading ? "Reading…" : "Upload .docx / .pdf"}
              </button>
              {filename !== "Pasted text.txt" && (
                <span className="flex items-center gap-1.5 text-sm text-zinc-500"><FileText size={14} /> {filename}</span>
              )}
            </div>
          </div>

          <label className="flex items-start gap-3 mt-4 p-4 rounded-xl border border-zinc-200 bg-zinc-50 cursor-pointer" data-testid="corpus-consent">
            <input type="checkbox" checked={consentCorpus} onChange={(e) => setConsentCorpus(e.target.checked)}
              data-testid="corpus-consent-checkbox" className="mt-0.5 h-4 w-4 accent-indigo-600 shrink-0" />
            <span className="text-sm text-zinc-500 leading-relaxed">
              <b className="text-zinc-800">Help improve MyAE.</b> I consent to my assignment being
              anonymised and securely stored to improve MyAE's similarity analysis. My work will
              never be publicly available or sold.
            </span>
          </label>

          <div className="flex items-center gap-3 mt-6">
            <button onClick={() => setStep(1)} data-testid="back-step-btn" className={`${btnOutline} h-12 px-5`}>
              <ArrowLeft size={18} /> Back
            </button>
            <button onClick={submit} data-testid="analyze-btn" className={`${btnPrimary} flex-1 h-12 text-base`}>
              <Sparkles size={18} /> Analyse My Essay
            </button>
          </div>
          {!question.trim() && (
            <p className="text-xs text-amber-600 mt-3 text-center" data-testid="no-brief-warning">
              No assignment brief added — you'll get writing feedback, but a predicted grade won't be shown.
              Go back to add the brief for an accurate grade.
            </p>
          )}
          <p className="text-xs text-zinc-400 mt-3 text-center">Google Docs import & public-web similarity coming soon.</p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Group Rubric Preset Picker — Phase 1 of Group Work support. Shown just
// above the "Marking rubric / criteria" textarea; picking a preset prefills
// the rubric. In-group comments and contribution tracking are deliberately
// out of scope here.
// ---------------------------------------------------------------------------
const GroupRubricPresetPicker = ({ onPick, currentRubric }) => {
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/group-rubric-presets`)
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => {});
  }, []);

  if (!presets.length) return null;

  const apply = (p) => {
    if (currentRubric && currentRubric.trim() && !window.confirm("Replace the current rubric text with this preset?")) return;
    onPick(p);
    setSelected(p.id);
    setOpen(false);
  };

  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="group-rubric-presets">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left text-sm text-zinc-800"
        data-testid="group-rubric-presets-toggle"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2">
          <Users size={14} className="text-indigo-600" />
          <span className="font-medium">Group assignment?</span>
          <span className="text-zinc-500">Use a preset rubric</span>
        </span>
        <span className="text-xs text-indigo-700">{open ? "Hide" : "Show presets"}</span>
      </button>
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="group-rubric-presets-list">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => apply(p)}
              className={`text-left rounded-lg border p-3 bg-white hover:border-indigo-400 transition-colors ${selected === p.id ? "border-indigo-500 ring-1 ring-indigo-200" : "border-zinc-200"}`}
              data-testid={`group-rubric-preset-${p.id}`}
            >
              <p className="text-sm font-medium text-zinc-900">{p.label}</p>
              <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{p.description}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {(p.columns || []).map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {c.replace("_", " ")}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
